import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { cleanState, EditorState, isUpdating } from '@ublend-npm/aula-editor';
import {
  Box,
  Button,
  IconButton,
  Icons,
  Modal,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';
import feedPostTemplates from '../../../Feed/CreatePost/feedPostTemplates';
import isEmptyContent from '../../../Feed/isEmptyContent';
import { createStyles } from './PostModal.styles';
import {
  CANCEL,
  CLOSE,
  DEFAULT_ITEM_CLASS,
  DEFAULT_POST_PLACEHOLDER,
  getModalTitle,
  ITEM_CLASS_MAP,
  MESSAGES,
  SAVE,
} from '../text';
import type { PostModalProps } from '../types';
import PostMobileWrapper from '../PostMobile/PostMobileWrapper';

const CLOSE_DELAY_MS = 1000;

const createCleanEditorState = (): EditorState =>
  JSON.parse(JSON.stringify(cleanState));

// This function is used to get the initial editor state from the content.
const getInitialEditorState = (content?: string): EditorState => {
  if (!content) {
    return createCleanEditorState();
  }

  try {
    const parsed = JSON.parse(content);
    if (!parsed?.value?.length) {
      return createCleanEditorState();
    }
    return parsed;
  } catch {
    return createCleanEditorState();
  }
};

const PostModal = ({
  open,
  spaceId,
  onClose = () => {},
  onSubmit,
  isSavingPost = false,
  editorConfig,
  EditorComponent,
  content,
  itemClass = DEFAULT_ITEM_CLASS,
  mode = 'edit',
  title,
  placeholder = DEFAULT_POST_PLACEHOLDER,
  footerExtra,
  handleBack = () => {},
  itemId,
}: PostModalProps) => {
  const theme = useTheme() as any;
  const styles = createStyles(theme);
  const isCreate = mode === 'create';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const initialEditorState = useMemo<EditorState>(
    () => getInitialEditorState(content),
    [content]
  );

  const [editorState, setEditorState] =
    useState<EditorState>(initialEditorState);
  // Passing a key to the editor component remounts Slate.
  const [editorKey, setEditorKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [enablePostButtons, setEnablePostButtons] = useState<boolean>(
    !isCreate
  );
  const [saving, setSaving] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const closeTimeout = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(closeTimeout.current), []);

  useEffect(() => {
    setIsSubmitting(isUpdating(editorState, editorConfig));
  }, [editorState, editorConfig]);

  const handleRequestClose = () => {
    if (saving) return;
    setEditorState(getInitialEditorState(content));
    setEditorKey((key) => key + 1);
    onClose();
  };

  const handleEditorChange = useCallback((state: EditorState) => {
    // If the editor is empty, set the editor state to the clean state
    if (!state?.value?.length) {
      setEditorState(createCleanEditorState());
      setEditorKey((key) => key + 1);
      return;
    }

    setEditorState(state);
  }, []);

  const statusMessages = MESSAGES[mode];

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await onSubmit(editorState);
      setMessage(statusMessages.success);
      closeTimeout.current = setTimeout(() => {
        setSaving(false);
        setMessage('');
        onClose();
      }, CLOSE_DELAY_MS);
    } catch (error) {
      setSaving(false);
      setMessage(statusMessages.error);
      console.log(error);
    } finally {
      if (isMobile) {
        handleBack();
      }
    }
  };

  // Show loading state when the post is being saved, the editor is submitting, or the saving state is true
  const loading = isSavingPost || isSubmitting || saving;
  const isPostItem = itemClass === DEFAULT_ITEM_CLASS;
  const itemLabel =
    ITEM_CLASS_MAP[itemClass] || ITEM_CLASS_MAP[DEFAULT_ITEM_CLASS];
  const analyticsType = isCreate
    ? 'createpost'
    : itemLabel === 'message'
    ? 'inbox'
    : itemLabel;
  const heading = title ?? getModalTitle(mode, itemLabel);

  // Disable the submit button when the post is being saved, the editor is submitting, or the saving state is true
  const contentIsEmpty =
    !editorState?.value?.length || isEmptyContent(editorState);
  const disableButton = saving || !enablePostButtons || contentIsEmpty;

  // Editor component
  const editor = (
    <>
      <Box
        sx={{
          ...styles.editorWrapper,
          p: saving || loading ? '0 0 0 3rem' : '0',
        }}
        data-testid="edit-post-editor"
      >
        <EditorComponent
          key={editorKey}
          type={analyticsType}
          config={editorConfig}
          editorState={editorState}
          placeholder={placeholder}
          onChange={handleEditorChange}
          isSubmitting={loading}
          setSubmittingState={setIsSubmitting}
          isAssignmentsEnabled
          templates={isPostItem ? feedPostTemplates : []}
          contentType={isPostItem ? 'post' : null}
          onFilesStatusChange={({
            isUpdating: filesUpdating,
            isPersistable: filesPersistable,
            hasError,
          }) => {
            setEnablePostButtons(
              !filesUpdating && !hasError && filesPersistable
            );
          }}
        />
      </Box>
    </>
  );

  // For mobile screens we have a standalone page for the post modal
  if (isMobile && open) {
    return (
      <PostMobileWrapper
        mode="edit"
        loading={loading}
        submitDisabled={disableButton}
        onSubmit={handleSubmit}
        onBack={handleBack}
        showUnsavedChangesAlert={!disableButton || contentIsEmpty}
      >
        {editor}
      </PostMobileWrapper>
    );
  }

  return (
    <Modal open={open} onClose={handleRequestClose}>
      <Box sx={styles.modalWrapper}>
        <Box sx={styles.modalHeader}>
          <Typography component="span" variant="h6">
            {heading}
          </Typography>
          <IconButton
            aria-label={CLOSE}
            onClick={handleRequestClose}
            sx={styles.closeButton}
          >
            <Tooltip title={CLOSE} disableInteractive>
              <Icons.Close />
            </Tooltip>
          </IconButton>
        </Box>
        {editor}
        <Box sx={styles.footer}>
          {footerExtra ? (
            <Box sx={styles.footerExtra}>{footerExtra}</Box>
          ) : null}
          <Button
            sx={{ ...styles.buttonSx, width: { xs: 'auto', sm: '90px' } }}
            kind="secondary"
            type="button"
            onClick={handleRequestClose}
            disabled={saving}
          >
            {CANCEL}
          </Button>
          <Button
            sx={styles.buttonSx}
            kind="primary"
            type="button"
            loading={saving}
            onClick={handleSubmit}
            disabled={disableButton}
          >
            {SAVE}
          </Button>
          <Box sx={styles.visuallyHidden} aria-live="polite" role="status">
            {message}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default PostModal;
